import { CircularProgress } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { AddProjectHook } from '../../Hook Code/AddProjectHook';
import AddIcon from '@mui/icons-material/Add';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useSidebarWidth } from '../../Utility/useSidebarWidth';

/* ─── Searchable Dropdown ────────────────────────────────────────────────── */
const SearchableSelect = ({ value, onChange, options, placeholder, isDark, labelKey = 'name', valueKey = '_id' }) => {
  const [open,   setOpen]   = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  const selected = options?.find(o => o[valueKey] === value);
  const filtered = (options || []).filter(o => o[labelKey]?.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const base = {
    width: '100%', padding: '13px 16px', borderRadius: '10px',
    border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
    backgroundColor: isDark ? '#151827' : '#f8fafc',
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: '14px', fontFamily: "'DM Sans', sans-serif",
    boxSizing: 'border-box', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    outline: 'none', transition: 'all 0.2s',
  };

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      <div style={base} onClick={() => setOpen(o => !o)}>
        <span style={{ color: selected ? (isDark ? '#f1f5f9' : '#0f172a') : (isDark ? '#4b5563' : '#94a3b8') }}>
          {selected ? selected[labelKey] : placeholder}
        </span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0, opacity: 0.5 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 9999,
          backgroundColor: isDark ? '#1e2132' : '#ffffff',
          border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
          borderRadius: '12px',
          boxShadow: isDark ? '0 16px 40px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.12)',
          overflow: 'hidden', animation: 'dd-open 0.15s ease-out both',
        }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${isDark ? '#2d3248' : '#f0f0f0'}` }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              backgroundColor: isDark ? '#151827' : '#f8fafc',
              border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
              borderRadius: '8px', padding: '8px 12px',
            }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: 0.5 }}>
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
                style={{ border: 'none', background: 'none', outline: 'none', fontSize: '13px', color: isDark ? '#f1f5f9' : '#0f172a', fontFamily: "'DM Sans', sans-serif", width: '100%' }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0, color: isDark ? '#6b7280' : '#94a3b8' }}>✕</button>
              )}
            </div>
          </div>
          <div style={{ maxHeight: '220px', overflowY: 'auto' }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8' }}>No results found</div>
            ) : filtered.map(o => (
              <div key={o[valueKey]}
                onClick={() => { onChange(o[valueKey]); setOpen(false); setSearch(''); }}
                style={{
                  padding: '11px 16px', fontSize: '14px', fontFamily: "'DM Sans', sans-serif",
                  cursor: 'pointer',
                  backgroundColor: value === o[valueKey] ? (isDark ? '#1d2a5e' : '#eef1fb') : 'transparent',
                  color: value === o[valueKey] ? (isDark ? '#90b4ff' : '#0d2a6e') : (isDark ? '#e5e7eb' : '#1f2937'),
                  fontWeight: value === o[valueKey] ? 600 : 400,
                  borderLeft: value === o[valueKey] ? '3px solid #1d4ed8' : '3px solid transparent',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (value !== o[valueKey]) e.currentTarget.style.backgroundColor = isDark ? '#252840' : '#f5f7ff'; }}
                onMouseLeave={e => { if (value !== o[valueKey]) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                {o[labelKey]}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Component ─────────────────────────────────────────────────────── */
// const EXPANDED_WIDTH = 242;

export const AddProjects = () => {
  const theme    = useTheme();
  const mode     = theme.palette.mode;
  const navigate = useNavigate();
  const isDark   = mode === 'dark';

  // const [sidebarWidth, setSidebarWidth] = useState(EXPANDED_WIDTH);
  const sidebarWidth = useSidebarWidth();

  // useEffect(() => {
  //   const h = (e) => setSidebarWidth(e.detail?.width ?? EXPANDED_WIDTH);
  //   window.addEventListener('sidebarResized', h);
  //   return () => window.removeEventListener('sidebarResized', h);
  // }, []);

  const [
    projectname, developername, areaname, startprice,
    onprojectnamechange, onchanegAreaName, onchangedevelopername, onStartPriceChange,
    onsubmitadd, resdevelopersdata, rescitiesdata, onchangecitychange, cityname, addProjectLoading,
  ] = AddProjectHook();

  /* ─── shared input style ─── */
  const inputStyle = {
    width: '100%', padding: '13px 16px', borderRadius: '10px',
    border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
    backgroundColor: isDark ? '#151827' : '#f8fafc',
    color: isDark ? '#f1f5f9' : '#0f172a',
    fontSize: '14px', outline: 'none', transition: 'all 0.2s',
    fontFamily: "'DM Sans',sans-serif", boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: 700,
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: isDark ? '#9ca3af' : '#64748b', marginBottom: '8px',
  };
  const onFocus = (e) => {
    e.target.style.borderColor      = '#2563eb';
    e.target.style.backgroundColor  = isDark ? '#1a1f35' : '#ffffff';
    e.target.style.boxShadow        = '0 0 0 3px rgba(37,99,235,0.12)';
  };
  const onBlur = (e) => {
    e.target.style.borderColor     = isDark ? '#2d3248' : '#e2e8f0';
    e.target.style.backgroundColor = isDark ? '#151827' : '#f8fafc';
    e.target.style.boxShadow       = 'none';
  };

  return (
    <>
      <style>{`
        @keyframes dd-open {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* ✅ wrapper: يبدأ بعد الـ sidebar مباشرةً ويملأ باقي الشاشة */}
      <div style={{
        position: 'fixed',
        top: '50px',
        left: sidebarWidth,
        right: 0,
        bottom: 0,
        transition: 'left 0.25s cubic-bezier(0.4,0,0.2,1)',
        overflowY: 'auto',
        background: isDark ? 'linear-gradient(135deg,#0f1117 0%,#1a1d2e 100%)' : '#f0f2f5',
        fontFamily: "'DM Sans','Segoe UI',sans-serif",
        marginTop:'10px'
      }}>

        {/* ── Breadcrumb ── */}
        <div style={{
          padding: '14px 28px',
          backgroundColor: isDark ? '#1a1d2e' : '#f0f2f5',
          display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
        }}>
          <Link to="/Project" style={{ textDecoration: 'none', color: isDark ? '#9ca3af' : '#6b7280', fontWeight: 500 }}>
            Projects
          </Link>
          <span style={{ color: isDark ? '#4b5563' : '#d1d5db' }}>/</span>
          <span style={{ color: isDark ? '#e5e7eb' : '#111827', fontWeight: 600 }}>Add New Project</span>
        </div>

        {/* ✅ المحتوى يبدأ من أعلى اليسار — padding فقط بدون centering */}
        <div style={{ padding: '28px 32px 48px' }}>

          <div style={{
            width: '100%',
            maxWidth: '720px',          /* ✅ يمتد بعرض كافٍ من اليسار */
            backgroundColor: isDark ? '#1e2132' : '#ffffff',
            borderRadius: '20px',
            padding: '36px 40px',
            boxShadow: isDark ? '0 24px 60px rgba(0,0,0,0.5)' : '0 4px 32px rgba(0,0,0,0.08)',
            border: isDark ? '1px solid #2a2f45' : '1px solid #e8eaf0',
          }}>

            {/* title */}
            <div style={{ marginBottom: '28px' }}>
              <h1 style={{ fontSize: '22px', fontWeight: 700, color: isDark ? '#f1f5f9' : '#0d1b3e', margin: '0 0 4px', letterSpacing: '-0.4px' }}>
                Create New Project
              </h1>
              <p style={{ fontSize: '13px', color: isDark ? '#6b7280' : '#94a3b8', margin: 0 }}>
                Initialize a new luxury asset in the CRM database
              </p>
            </div>

            {/* fields grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>

              {/* Project Name */}
              <div>
                <label style={labelStyle}>Project Name *</label>
                <input style={inputStyle} value={projectname} onChange={onprojectnamechange}
                  type="text" placeholder="e.g. Azure Heights" onFocus={onFocus} onBlur={onBlur} />
              </div>

              {/* Developer */}
              <div>
                <label style={labelStyle}>Developer *</label>
                <SearchableSelect
                  value={developername}
                  onChange={(val) => onchangedevelopername({ target: { value: val } })}
                  options={resdevelopersdata?.data || []}
                  placeholder="Select developer"
                  isDark={isDark}
                />
              </div>

              {/* City */}
              <div>
                <label style={labelStyle}>City *</label>
                <SearchableSelect
                  value={cityname}
                  onChange={(val) => onchangecitychange({ target: { value: val } })}
                  options={rescitiesdata?.data || []}
                  placeholder="Select city"
                  isDark={isDark}
                />
              </div>

              {/* Area */}
              <div>
                <label style={labelStyle}>Area Name *</label>
                <input style={inputStyle} value={areaname} onChange={onchanegAreaName}
                  type="text" placeholder="e.g. Downtown Core" onFocus={onFocus} onBlur={onBlur} />
              </div>

              {/* Start Price — full width */}
              <div style={{ gridColumn: '1 / span 2' }}>
                <label style={labelStyle}>Start Price (Optional)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{
                    position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)',
                    color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600, fontSize: '15px', zIndex: 1,
                  }}>$</span>
                  <input style={{ ...inputStyle, paddingLeft: '36px' }}
                    value={startprice} onChange={onStartPriceChange}
                    type="number" placeholder="0.00" min="0"
                    onFocus={onFocus} onBlur={onBlur}
                  />
                </div>
              </div>

            </div>

            {/* divider */}
            <div style={{ height: '1px', backgroundColor: isDark ? '#2a2f45' : '#e8eaf0', margin: '28px 0' }} />

            {/* buttons */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={!addProjectLoading ? onsubmitadd : null}
                disabled={addProjectLoading}
                type="button"
                style={{
                  flex: 1, padding: '14px 24px', borderRadius: '10px', border: 'none',
                  background: 'linear-gradient(135deg,#1e3a8a,#1d4ed8)',
                  color: '#fff', fontSize: '13px', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: addProjectLoading ? 'not-allowed' : 'pointer',
                  opacity: addProjectLoading ? 0.7 : 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  transition: 'all 0.2s', boxShadow: '0 4px 16px rgba(29,78,216,0.35)',
                  fontFamily: "'DM Sans',sans-serif",
                }}
                onMouseEnter={(e) => { if (!addProjectLoading) { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 6px 24px rgba(29,78,216,0.45)'; } }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(29,78,216,0.35)'; }}
              >
                {addProjectLoading
                  ? <><CircularProgress size={16} style={{ color: '#fff' }} /><span>Creating…</span></>
                  : <><AddIcon sx={{ fontSize: 17 }} /><span>Create Project</span></>}
              </button>

              <button
                type="button"
                onClick={() => navigate('/Project')}
                style={{
                  padding: '14px 28px', borderRadius: '10px',
                  border: `1.5px solid ${isDark ? '#2d3248' : '#e2e8f0'}`,
                  backgroundColor: 'transparent',
                  color: isDark ? '#9ca3af' : '#64748b',
                  fontSize: '13px', fontWeight: 700,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  cursor: 'pointer', transition: 'all 0.2s',
                  fontFamily: "'DM Sans',sans-serif",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = isDark ? '#4b5563' : '#94a3b8'; e.currentTarget.style.color = isDark ? '#e5e7eb' : '#374151'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = isDark ? '#2d3248' : '#e2e8f0'; e.currentTarget.style.color = isDark ? '#9ca3af' : '#64748b'; }}
              >
                Cancel
              </button>
            </div>

          </div>
        </div>
      </div>
    </>
  );
};
